import React, { useEffect, useState, useCallback } from "react";
import { warehousesAPI } from "../../services/api";
import toast from "react-hot-toast";
import { Plus, Pencil, Trash2, FileJson, Tags } from "lucide-react";
import JsonImportBox from "../../components/JsonImportBox";
import Pagination from "../../components/Pagination";
import { monthLabel } from "../../utils/month";
import { localDateStr } from "../../utils/date";

interface ImportItem { _id: string; name: string; unit: string; quantity: number; note: string; price: number; date: string; }
interface ImportRecord { items: ImportItem[]; totalSpend: number; }

const INPUT = "w-full px-3 py-2 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-coffee-400";

function Th({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <th className={`px-4 py-2.5 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wide bg-zinc-50 ${className}`}>{children}</th>;
}
function Td({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-4 py-3 text-sm text-zinc-700 ${className}`}>{children}</td>;
}

function toDateInput(iso?: string) {
  if (!iso) return localDateStr();
  return new Date(iso).toISOString().slice(0, 10);
}
function formatMoney(n: number) {
  return n.toLocaleString("vi-VN") + "đ";
}
function formatDate(iso: string) {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}

export default function ImportPanel({ warehouseId, month }: { warehouseId: string; month: string }) {
  const [record, setRecord]       = useState<ImportRecord | null>(null);
  const [loading, setLoading]     = useState(false);
  const [adding, setAdding]       = useState(false);
  const [jsonMode, setJsonMode]   = useState(false);
  const [priceFixMode, setPriceFixMode] = useState(false);
  const [editItem, setEditItem]   = useState<ImportItem | null>(null);
  const [page, setPage]           = useState(1);
  const [pageSize, setPageSize]   = useState(10);
  const [form, setForm]           = useState({ name: "", unit: "", quantity: "1", note: "", price: "0", date: toDateInput() });
  const [saving, setSaving]       = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await warehousesAPI.getImport(warehouseId, month); setRecord(res.data.data); }
    catch { toast.error("Lỗi tải nhập hàng"); }
    finally { setLoading(false); }
  }, [warehouseId, month]);

  useEffect(() => { load(); setPage(1); }, [load]);

  const openAdd = () => { setForm({ name: "", unit: "", quantity: "1", note: "", price: "0", date: toDateInput() }); setEditItem(null); setAdding(true); };
  const openEdit = (item: ImportItem) => {
    setForm({ name: item.name, unit: item.unit, quantity: String(item.quantity), note: item.note, price: String(item.price || 0), date: toDateInput(item.date) });
    setEditItem(item); setAdding(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nhập tên hàng"); return; }
    setSaving(true);
    try {
      const data = {
        name: form.name, unit: form.unit, quantity: Number(form.quantity) || 1, note: form.note,
        price: Number(form.price) || 0, date: form.date,
      };
      const res = editItem
        ? await warehousesAPI.updateImportItem(warehouseId, month, editItem._id, data)
        : await warehousesAPI.addImportItem(warehouseId, month, data);
      setRecord(res.data.data);
      setAdding(false);
      toast.success(editItem ? "Đã cập nhật" : "Đã thêm");
    } catch { toast.error("Lỗi lưu"); } finally { setSaving(false); }
  };

  const del = async (item: ImportItem) => {
    if (!window.confirm(`Xoá "${item.name}"?`)) return;
    try {
      const res = await warehousesAPI.deleteImportItem(warehouseId, month, item._id);
      setRecord(res.data.data);
      toast.success("Đã xoá");
    } catch { toast.error("Lỗi xoá"); }
  };

  const items = record?.items || [];
  const paged = items.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-bold text-zinc-800">{monthLabel(month)}</span>
        {!loading && (
          <span className="text-sm font-semibold text-amber-700">
            Tổng chi: {formatMoney(record?.totalSpend || 0)}
          </span>
        )}
      </div>

      {loading ? (
        <p className="text-xs text-zinc-400 text-center py-3">Đang tải...</p>
      ) : (
        <div className="space-y-3">
          <div className="overflow-x-auto rounded-xl border border-amber-100">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-amber-100">
                  <Th className="w-8">#</Th>
                  <Th>Tên hàng</Th>
                  <Th className="w-20 text-center">ĐVT</Th>
                  <Th className="w-20 text-center">SL</Th>
                  <Th className="w-28 text-right">Đơn giá</Th>
                  <Th className="w-28 text-right">Thành tiền</Th>
                  <Th className="w-24 text-center">Ngày nhập</Th>
                  <Th>Ghi chú</Th>
                  <Th className="w-20 text-center">Thao tác</Th>
                </tr>
              </thead>
              <tbody>
                {paged.length === 0 && !adding ? (
                  <tr><td colSpan={9} className="px-4 py-8 text-center text-sm text-zinc-400">Chưa có hàng nhập trong {monthLabel(month)}</td></tr>
                ) : paged.map((item, i) => (
                  <tr key={item._id} className="border-b border-amber-50 hover:bg-amber-50 transition-colors">
                    <Td className="text-zinc-400 text-xs">{(page - 1) * pageSize + i + 1}</Td>
                    <Td className="font-semibold text-zinc-800">{item.name}</Td>
                    <Td className="text-center text-zinc-500">{item.unit || "—"}</Td>
                    <Td className="text-center font-bold text-amber-700">{item.quantity}</Td>
                    <Td className="text-right text-zinc-600">{formatMoney(item.price || 0)}</Td>
                    <Td className="text-right font-semibold text-zinc-800">{formatMoney(Math.round((item.price || 0) * item.quantity))}</Td>
                    <Td className="text-center text-zinc-500 text-xs">{formatDate(item.date)}</Td>
                    <Td className="text-zinc-400 text-xs italic">{item.note || "—"}</Td>
                    <Td className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => openEdit(item)} className="p-1.5 text-zinc-300 hover:text-indigo-600 touch-manipulation"><Pencil size={13}/></button>
                        <button onClick={() => del(item)}      className="p-1.5 text-zinc-300 hover:text-red-500 touch-manipulation"><Trash2 size={13}/></button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} total={items.length} pageSize={pageSize} onChange={setPage} onPageSizeChange={s => { setPageSize(s); setPage(1); }} />

          {jsonMode ? (
            <JsonImportBox
              fields={[
                { key: "name",     label: "Tên hàng hoá",              required: true },
                { key: "unit",     label: "Đơn vị tính (cái, chai, hộp...)" },
                { key: "quantity", label: "Số lượng nhập (mặc định: 1)" },
                { key: "price",    label: "Đơn giá (mặc định: 0)" },
                { key: "date",     label: "Ngày nhập YYYY-MM-DD (mặc định: hôm nay)" },
                { key: "note",     label: "Ghi chú (nguồn gốc, loại...)" },
              ]}
              example={`[\n  {"name": "Khăn tắm", "unit": "cái", "quantity": 20, "price": 45000, "note": "loại dày 70x140"},\n  {"name": "Dầu gội", "unit": "chai", "quantity": 10, "price": 25000}\n]`}
              onImport={items => warehousesAPI.bulkAddImportItems(warehouseId, month, items).then(res => setRecord(res.data.data))}
              onClose={() => setJsonMode(false)}
            />
          ) : priceFixMode ? (
            <JsonImportBox
              title="Sửa giá hàng theo tên"
              fields={[
                { key: "name",  label: "Tên hàng hoá — phải khớp đúng tên đang có trong tháng này", required: true },
                { key: "price", label: "Giá thật (đ)", required: true },
              ]}
              example={`[\n  {"name": "Khăn tắm", "price": 45000},\n  {"name": "Dầu gội", "price": 25000}\n]`}
              onImport={items => warehousesAPI.bulkUpdateImportPrices(warehouseId, month, items).then(res => {
                setRecord(res.data.data);
                if (res.data.notFoundNames?.length) {
                  toast.error(`Không khớp tên nào trong tháng này: ${res.data.notFoundNames.join(", ")}`);
                }
              })}
              successMessage={n => `Đã đối chiếu ${n} tên hàng`}
              onClose={() => setPriceFixMode(false)}
            />
          ) : adding ? (
            <div className="bg-white border border-zinc-200 rounded-xl p-3 space-y-2 mt-1">
              <input className={INPUT} placeholder="Tên hàng *" value={form.name} autoFocus
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              <div className="grid grid-cols-2 gap-2">
                <input className={INPUT} placeholder="ĐVT (cái, hộp...)" value={form.unit}
                  onChange={e => setForm(f => ({ ...f, unit: e.target.value }))} />
                <input type="number" inputMode="numeric" min={0} className={INPUT} placeholder="Số lượng" value={form.quantity}
                  onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input type="number" inputMode="numeric" min={0} className={INPUT} placeholder="Đơn giá (đ)" value={form.price}
                  onChange={e => setForm(f => ({ ...f, price: e.target.value }))} />
                <input type="date" className={INPUT} value={form.date}
                  onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
              </div>
              <input className={INPUT} placeholder="Ghi chú (tuỳ chọn)" value={form.note}
                onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />
              <div className="flex gap-2 pt-1">
                <button onClick={save} disabled={saving}
                  className="flex-1 py-2 bg-amber-500 text-white rounded-lg text-sm font-semibold disabled:opacity-50 touch-manipulation">
                  {saving ? "Đang lưu..." : editItem ? "Cập nhật" : "Thêm hàng"}
                </button>
                <button onClick={() => setAdding(false)}
                  className="px-4 py-2 border border-zinc-200 rounded-lg text-sm text-zinc-600 touch-manipulation">Huỷ</button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <button onClick={openAdd}
                className="flex items-center gap-1.5 text-sm text-amber-600 font-medium py-1 touch-manipulation">
                <Plus size={14} /> Thêm hàng nhập
              </button>
              <button onClick={() => setJsonMode(true)}
                className="flex items-center gap-1.5 text-sm text-indigo-500 font-medium py-1 touch-manipulation">
                <FileJson size={14} /> Nhập JSON
              </button>
              <button onClick={() => setPriceFixMode(true)}
                className="flex items-center gap-1.5 text-sm text-emerald-600 font-medium py-1 touch-manipulation">
                <Tags size={14} /> Sửa giá theo tên
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
